'use client';
import { CaptchaAction } from '@eliadmin/core/src/api/recaptcha-actions';

import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';

import { ValidationError } from '@eliadmin/core/src/api/errors';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Checkbox } from '@eliadmin/core/src/components/ui/checkbox';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@eliadmin/core/src/components/ui/form';
import { Textarea } from '@eliadmin/core/src/components/ui/textarea';
import { toast } from '@eliadmin/core/src/components/ui/use-toast';
import { useFormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { useVariantsStore } from '@eliadmin/core/src/stores/variant-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import Link from '@eliadmin/core/src/components/link';

import { useForm } from 'react-hook-form';
import { z } from 'zod';
import type { FormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import {
  ResponsiveDialogContent,
  useResponsiveDialogStore,
  ResponsiveDialogFooter,
  ResponsiveDialogTitle,
} from '@eliadmin/core/src/components/ui/responsive-dialog';
import { Field } from '@eliadmin/core/src/components/form/field';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import { HtmlDescription } from '../../homepage/shared/html-description';
import { RiSendPlane2Line } from 'react-icons/ri';
import { sendProductInterestMessage as sendProductInterestMessageCore } from '@eliadmin/core/src/api/forms/forms-api';
const sendProductInterestMessage = (req: Parameters<typeof sendProductInterestMessageCore>[0]) =>
  sendProductInterestMessageCore({ ...req, captchaAction: CaptchaAction.INTEREST });

export const ASK_FOR_PRODUCT_DIALOG_ID = 'ASK_FOR_PRODUCT_DIALOG_ID';

const askAboutProductSchema = (VM: FormValidationMessages) =>
  z.object({
    category: z.string().min(1).max(255),
    product: z.string().min(1).max(255),
    name: z.string().min(1).max(255),
    surname: z.string().min(1).max(255),
    email: z.string().email().min(1).max(255),
    phone: z.string().min(1).max(255),
    text: z.string().min(1).max(1000),
    gdpr: z.boolean().refine(val => val === true, { message: VM.GDPR_VALIDATION_MESSAGE() }),
    ['g-recaptcha-response']: z.string().optional(),
  });

export type AskAboutProductFormValues = z.infer<ReturnType<typeof askAboutProductSchema>>;

export function ProductAskDialog() {
  const { t } = useTranslations();
  const locale = useLocaleStore(s => s.currentLocale);

  const product = useVariantsStore(s => s.product);
  const me = useCustomerStore(s => s.me?.customer);

  const askMutation = useMutation({ mutationFn: sendProductInterestMessage });

  const { formValidationMessages } = useFormValidationMessages(t);

  const form = useForm<z.infer<ReturnType<typeof askAboutProductSchema>>>({
    resolver: zodResolver(askAboutProductSchema(formValidationMessages)),
    defaultValues: {
      category: product?.categories?.[0]?.name ?? '',
      product: product.name,
      name: '',
      surname: '',
      email: '',
      phone: '',
      text: '',
      gdpr: false,
    },
  });
  const close = useResponsiveDialogStore(state => state.close);

  const handleSubmit = form.handleSubmit(async data => {
    try {
      await askMutation.mutateAsync({
        locale,
        data: {
          ...data,
        },
      });
      toast({
        title: t('text659'),
        variant: 'success',
      });
      close(ASK_FOR_PRODUCT_DIALOG_ID);
    } catch (err) {
      // handle 422
      if (err instanceof ValidationError) {
        for (const [field, errors] of Object.entries(err.fields)) {
          form.setError(field as any, { type: 'manual', message: errors.join('\n') });
        }
        return;
      }

      toast({
        title: t('text660'),
        description: (err as any)?.message,
        variant: 'destructive',
      });
    }
  });

  return (
    <ResponsiveDialogContent
      dialogProps={{
        'aria-describedby': undefined,
        className:
          'max-w-screen max-h-[94vh] h-fit w-[700px] overflow-y-scroll sm:max-w-[700px] !py-16 md:max-w-[700px] px-6 rounded-none border-none flex flex-col items-center justify-center gap-8 ea-product-ask-dialog__dialog hide-scroll-bar',
      }}
      drawerProps={{
        'aria-describedby': undefined,
        className: 'max-h-[82vh] rounded-none border-none px-4',
      }}
    >
      <div className="hide-scroll-bar flex w-full flex-col gap-8 overflow-auto pb-4 pt-4 sm:max-w-[540px] sm:items-center sm:justify-start sm:pt-0">
        <ResponsiveDialogTitle
          className={cn(
            'w-full uppercase',
            'font-medum pt-16 text-center text-2xl text-foreground sm:pt-0 sm:text-3xl',
          )}
        >
          {t('text1361')}
        </ResponsiveDialogTitle>
        <HtmlDescription
          content={`<p>${t('text1362')}</p>`}
          className="w-full text-center"
          color="gray"
          size="large"
        />
        <div className="flex w-full items-start justify-between border border-[#EDEDED] bg-white p-4">
          <div className="flex w-full items-center justify-start gap-4">
            <Image
              src={product?.image?.url ?? '/images/placeholder.png'}
              alt={resolveImageAlt(product?.image, product?.name)}
              height={0}
              width={0}
              quality={100}
              sizes="200px"
              className="h-[100px] w-[80px] object-cover object-center"
            />
            <div className="flex w-full flex-row items-start justify-between">
              <div className="flex flex-col items-start justify-start gap-1">
                {product.categories && (
                  <h4 className="text-base font-light uppercase text-foreground sm:text-lg">
                    {product.categories[0]?.name}
                  </h4>
                )}
                <p className="text-lg font-medium uppercase text-foreground sm:text-xl lg:text-2xl">
                  {product.name}
                </p>
              </div>
              {/* <Image
                src={'/images/placeholder.png'}
                alt="Manufacturer"
                height={0}
                width={0}
                className="h-6 w-fit object-contain object-center sm:h-8"
                sizes="200px"
              /> */}
            </div>
          </div>
        </div>
        <Form {...form}>
          <form
            onSubmit={handleSubmit}
            className={cn(
              'flex w-full flex-col gap-6 sm:grid sm:grid-cols-2 sm:gap-4',
              'ea-product-detail-ask-dialog',
            )}
          >
            <Field
              control={form.control}
              name="name"
              className={{ wrapper: 'col-span-1 w-full' }}
              placeholder={t('text33') + ' *'}
              required
              type="text"
            />
            <Field
              control={form.control}
              name="surname"
              className={{ wrapper: 'col-span-1 w-full' }}
              placeholder={t('text34') + ' *'}
              required
              type="text"
            />
            <Field
              control={form.control}
              name="email"
              className={{ wrapper: 'col-span-1 w-full sm:col-span-2' }}
              placeholder={t('text35') + ' *'}
              required
              type="email"
            />
            <Field
              control={form.control}
              name="phone"
              className={{ wrapper: 'col-span-2 w-full' }}
              placeholder={t('text36') + ' *'}
              required
              type="tel"
            />
            <FormField
              control={form.control}
              name={'text'}
              render={({ field }) => (
                <FormItem required className={cn('col-span-1 w-full sm:col-span-2')}>
                  {/* <FormLabel>
                <span>{t('text80')}</span>
              </FormLabel> */}
                  <FormControl>
                    <div
                      className={cn(
                        'w-full',
                        'ea-contact-form__input-wrapper ea-contact-form__text-input-wrapper',
                      )}
                    >
                      <Textarea placeholder={t('text615')} {...(field as any)} rows={5} />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="gdpr"
              render={({ field }) => (
                <FormItem
                  className={cn(
                    'col-span-1 flex flex-row items-start space-x-3 space-y-0 sm:col-span-2',
                    'ea-product-detail-ask-dialog__form-item-condition-accepted',
                  )}
                >
                  <FormControl
                    className={cn(
                      '',
                      'ea-product-detail-ask-dialog__form-item-condition-accepted-control',
                    )}
                  >
                    <Checkbox
                      ref={field.ref}
                      name={field.name}
                      disabled={field.disabled}
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      className={cn(
                        '',
                        'ea-product-detail-ask-dialog__form-item-condition-accepted-checkbox rounded-none',
                      )}
                    />
                  </FormControl>
                  <FormLabel
                    className={cn(
                      '',
                      'ea-product-detail-ask-dialog__form-item-condition-accepted-labes',
                    )}
                  >
                    {t('text264')}
                    &nbsp;
                    <Link
                      href={t('text391') ?? '#'}
                      target="_blank"
                      className={cn(
                        'underline',
                        'ea-product-detail-ask-dialog__form-item-condition-accepted-link1',
                      )}
                      prefetch={false}
                    >
                      {t('text265')}
                    </Link>
                    ,&nbsp;
                    <Link
                      href={t('text392') ?? '#'}
                      target="_blank"
                      className={cn(
                        'underline',
                        'ea-product-detail-ask-dialog__form-item-condition-accepted-link2',
                      )}
                      prefetch={false}
                    >
                      {t('text436')}
                    </Link>
                  </FormLabel>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name={'g-recaptcha-response'}
              render={() => (
                <FormItem required className={cn('w-full')}>
                  <FormMessage />
                </FormItem>
              )}
            />
            <ResponsiveDialogFooter
              className={cn(
                'col-span-1 flex w-full items-center justify-center sm:col-span-2',
                'ea-product-detail-ask-dialog__footer',
              )}
            >
              <Button
                type="submit"
                className={cn('ea-product-detail-ask-dialog__footer-button mx-auto')}
                // disabled={!form.formState.isValid || form.formState.isSubmitting}
                // isLoading={form.formState.isSubmitting || form.formState.isSubmitSuccessful}
              >
                {t('text185')} <RiSendPlane2Line size={18} className="ml-2" />
              </Button>
            </ResponsiveDialogFooter>
            {/* <p className="col-span-1 w-full text-xs text-[#999999] sm:col-span-2">
            Odoslaním kontaktného formuláru súhlasíte so spracovaním osobných údajov pre účely
            kontaktovania.
          </p> */}
          </form>
        </Form>
      </div>
    </ResponsiveDialogContent>
  );
}

ProductAskDialog.id = ASK_FOR_PRODUCT_DIALOG_ID;
